import { observable, makeObservable } from 'mobx'
import i18n from 'i18next'
import * as R from 'ramda'
import { sortBy } from '../utils/helpers'

class QuestionMenuStore {
    @observable filteredList = []

    productTypes = []

    @observable respondentTypes = []

    @observable questionCategories = []

    @observable selectedQuestionCategory = {
        label: 'All',
        value: '',
    }

    @observable showActive = true

    selectedSurveyTypes = []

    selectedRespondentTypes = []

    selectedProductTypes = []

    industries = []

    respondentTypesData = []

    questions = []

    surveyTypes = {
        PE: {
            label: 'Benchmarking',
            selected: true,
        },
        GE: {
            label: 'General',
            selected: true,
        },
    }

    @observable questionToDelete

    constructor(store) {
        makeObservable(this)
        this.appStore = store
    }

    init = async () => {
        try {
            const { isExploriClient } = this.appStore
            const questionsData = await this.getQuestionsData()
            this.questions = this.formatQuestionsData(questionsData)
            this.filteredList = this.questions

            this.industries = await this.getIndustriesData()
            this.industryCodes = this.industries.map((industry) => industry.code)
            if (!R.isEmpty(this.industryCodes)) {
                this.productTypes = await this.getProductTypesData(this.industryCodes)
                this.respondentTypesData = await this.getRespondentTypesData(this.industryCodes)
                this.respondentTypes = this.formatRespondentTypes(
                    this.industryCodes, this.respondentTypesData,
                )
            }

            if (isExploriClient()) {
                await this.getCategories()
            }
            this.questionCategory = await this.getCategory()
        } catch (error) {
            console.log(`QuestionMenuStore ${error}`)
        }
    }

    getQuestionsData = async (isActive = true) => {
        const endpoint = `/qlist/getquestions?&cols=questionList&criteria=questionList&isActive=${isActive ? '1' : '0'}`
        return this.appStore.http.get(endpoint)
    }

    formatQuestionsData = (questionsData) => {
        const questions = new Map()
        questionsData.forEach((question) => {
            if (!questions.has(question.questionId)) {
                question.productTypes = ''
                question.respondentTypes = ''
                question.surveyTypes = ''
                questions.set(question.questionId, question)
            }

            const q = questions.get(question.questionId)
            q.productTypes += `|${question.producttypecode}|`
            q.respondentTypes += `|${question.respondenttypecode}|`
            q.surveyTypes += `|${question.surveytypecode}|`
        })
        return Array.from(questions).map(([_, question]) => question)
    }

    getIndustriesData = async () => (
        this.appStore.http.get('/client/industry')
    )

    getProductTypesData = async (industries) => {
        const productTypes = await Promise.all(
            industries.map((industry) => this.appStore.http.get(`/producttype/${industry}`)),
        )

        return productTypes.flat().reduce((map, { name, code }) => {
            map[code] = {
                label: name,
                selected: true,
            }
            return map
        }, {})
    }

    getCategories = async () => {
        const questionCategoriesData = await this.appStore.http.get('/question/categories')

        this.questionCategories = [
            {
                label: 'All',
                value: '',
            },
            ...sortBy(questionCategoriesData, 'description').map((category) => ({
                value: category.categoryId,
                label: category.description,
            })),
        ]
    }

    getCategory = async () => {
        const { clientId } = this.appStore.tokenData
        const questionCategoryData = await this.appStore.http.get(`/qlist/${clientId}/categories`)
        return questionCategoryData.find((category) => category.description === 'UNCATEGORISED')
    }

    formatRespondentTypes = (industryCodes, respondentTypes) => {
        const industryNames = {}
        this.industries.forEach((i) => {
            industryNames[i.code] = i.name
        })
        return respondentTypes
            .filter(({ industry }) => industryCodes.includes(industry))
            .reduce((map,
                { code, name, industry }) => {
                map[code] = {
                    selected: true,
                    label: `${industryNames[industry]} - ${name}`,
                }
                return map
            }, {})
    }

    getRespondentTypesData = async () => {
        const { clientId } = this.appStore.tokenData
        return this.appStore.http.get(`/client/${clientId}/respondenttype`)
    }

    toggleIsActive = async ({ value }) => {
        this.showActive = value
        const questionsData = await this.getQuestionsData(value)
        this.questions = this.formatQuestionsData(questionsData)
        this.filteredList = this.questions
    }

    toggleSurveyType = ({ value, name }) => {
        const typeName = name.split('-')[1]
        this.surveyTypes[typeName].selected = value
        this.search()
    }

    toggleRespondentType = ({ value, name }) => {
        const typeName = name.split('-')[1]
        this.respondentTypes[typeName].selected = value
        this.search()
    }

    toggleProductType = ({ value, name }) => {
        const typeName = name.split('-')[1]
        this.productTypes[typeName].selected = value
        this.search()
    }

    search = () => {
        const {
            productTypes, respondentTypes, surveyTypes, questions,
        } = this
        const { searchStr } = this.searchModule

        const activeFiltered = questions
            .filter(({
                producttypecode, surveytypecode, respondenttypecode, questioncategory,
            }) => (
                (R.pathOr(true, [producttypecode, 'selected'])(productTypes))
                && (R.pathOr(true, [surveytypecode, 'selected'])(surveyTypes))
                && (R.pathOr(true, [respondenttypecode, 'selected'])(respondentTypes))
                && (this.selectedQuestionCategory.label === 'All' || questioncategory === this.selectedQuestionCategory.label)
            ))

        if (searchStr === '') {
            this.filteredList = activeFiltered
        } else {
            const re = new RegExp(searchStr, 'i')
            this.filteredList = activeFiltered
                .filter((d) => (
                    d.title.match(re)
                        || d.questionId.match(re)
                        || (R.hasPath(['questionType'], d) && d.questionType.match(re))
                        || d.questioncategory.match(re)
                ))
        }
    }

    editQuestion = (response) => {
        console.log(response)
        const question = this.questions
            .find((q) => (
                q.questionId.toUpperCase() === response.questionId.toUpperCase()
            ))
        question.title = response.title
        console.log(response.questionRespondents)
        question.productTypes = ''
        question.respondentTypes = ''
        question.surveyTypes = ''
        question.questioncategory = response.category
        response.questionRespondents.forEach((respondent) => {
            const [industrycode,producttypecode, surveytypecode, respondenttypecode] = respondent.split('-')
            question.productTypes += `|${producttypecode}|`
            question.respondentTypes += `|${respondenttypecode}|`
            question.surveyTypes += `|${surveytypecode}|`
        })
        this.search()
    }

    changeQuestionCategory = (obj) => {
        this.selectedQuestionCategory = obj
        this.search()
    }

    deactivateQuestion = async (questionId) => {
        try {
            const res = await this.appStore.http.delete(`/surveyBuilder/question/${questionId}`)
            if (res === true) {
                this.appStore.addNotification({ body: i18n.t('questionMenu.deleteQuestion.success'), level: 'success' })
                this.optimisticallyRemoveQuestion(questionId)
            } else {
                this.appStore.handleError({ body: i18n.t('questionMenu.deleteQuestion.error'), level: 'error' })
                this.questionToDelete = null
            }
        } catch (err) {
            console.error(err)
            this.appStore.handleError({ body: i18n.t('questionMenu.deleteQuestion.error'), level: 'error' })
            this.questionToDelete = null
        }
    }

    optimisticallyRemoveQuestion = (qId) => {
        const index = this.questions.findIndex((q) => (
            q.questionId.toUpperCase() === qId.toUpperCase()
        ))
        if (index > -1) {
            this.questions.splice(index, 1)
            this.search()
        }
    }

    addQuestion = ({
        questionId,
        title,
        questionRespondents,
        category,
    }) => {
        const question = {
            questionId,
            title,
        }
        question.productTypes = ''
        question.respondentTypes = ''
        question.surveyTypes = ''
        question.questioncategory = category
        questionRespondents.forEach((respondent) => {
            const [industrycode, producttypecode, surveytypecode, respondenttypecode] = respondent.split('-')
            question.productTypes += `|${producttypecode}|`
            question.respondentTypes += `|${respondenttypecode}|`
            question.surveyTypes += `|${surveytypecode}|`
        })
        this.questions.push(question)
        this.questions.sort((a, b) => {
            const textA = a.title?.toUpperCase()
            const textB = b.title?.toUpperCase()
            return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        })
        this.search()
    }

    resetFilter = () => {
        Object.keys(this.respondentTypes).forEach((key) => {
            this.respondentTypes[key].selected = true
        })
        Object.keys(this.surveyTypes).forEach((key) => {
            this.surveyTypes[key].selected = true
        })
        Object.keys(this.productTypes).forEach((key) => {
            this.productTypes[key].selected = true
        })
        this.selectedQuestionCategory = { label: 'All', value: '' }
        this.search()
    }
}

export default QuestionMenuStore
